import { CountrySheet } from 'pleinchamp-api-client';
import React, { ComponentPropsWithRef, FC } from 'react';
import { BasicLink } from '@components/buttons/BasicLink/BasicLink.component';
import { PlcImgLoader } from '@components/Loaders/PlcImgLoader.component';
import { PlcTextListLoader } from '@components/Loaders/PlcTextLoader.component';
import { PlcImg } from '@components/PlcImg/PlcImg.component';
import { Text } from '@components/Text/Text.component';
import { PlcList } from '@layout/components/PlcList/PlcList.component';
import classnames from 'classnames';

type Props = ComponentPropsWithRef<'ul'> & {
  sheets: CountrySheet[];
};

const COUNTRY_IMG_HEIGHT = 26;
const COUNTRY_IMG_WIDTH = 39;

const CountrySheetsList: FC<Props> = ({ sheets, className, ...rest }) => {
  const rootClass = classnames(className);
  if (!sheets.length) {
    return <Text i18nKey="services.country-sheets.no-country" variant="p" />;
  }

  return (
    <PlcList className={rootClass} {...rest}>
      {sheets.map(row => (
        <li key={row.countryCode} className="flex space-between align-center">
          <div className="flex align-center">
            <div
              className="plc-mr-m"
              style={{ height: COUNTRY_IMG_HEIGHT, overflow: 'hidden', width: COUNTRY_IMG_WIDTH }}
            >
              <PlcImg
                alt={row.flagImage.alt}
                loader={
                  <PlcImgLoader
                    dark
                    fixedWidth
                    height={COUNTRY_IMG_HEIGHT}
                    uniqueKey={`country-img-${row.countryCode}`}
                    width={COUNTRY_IMG_WIDTH}
                    withSun={false}
                  />
                }
                plcSrc={{ src: row.flagImage.url, type: 'external' }}
                style={{ height: COUNTRY_IMG_HEIGHT }}
                unloader={<div style={{ height: COUNTRY_IMG_HEIGHT, width: COUNTRY_IMG_WIDTH }} />}
              />
            </div>
            <Text tag="div" variant="h6">
              {row.countryName}
            </Text>
          </div>
          <BasicLink
            href={row.file.url}
            isUnderlined
            rel="noopener noreferrer"
            target="_blank"
            textProps={{ flavour: 'grey', i18nKey: 'services.country-sheets.check-pdf', variant: 'tiny' }}
          />
        </li>
      ))}
    </PlcList>
  );
};

const CountryItemLoader = () => (
  <li className="flex align-center">
    <PlcImgLoader
      className="plc-mr-m"
      dark
      fixedWidth
      height={COUNTRY_IMG_HEIGHT}
      uniqueKey="country-img"
      width={COUNTRY_IMG_WIDTH}
      withSun={false}
    />
    <PlcTextListLoader fixedWidth textProps={[{ variant: 'h6', width: 300 }]} uniqueKey="country-txt" />
  </li>
);

const CountrySheetsListLoader: FC<ComponentPropsWithRef<'ul'>> = ({ className, ...rest }) => {
  const rootClass = classnames(className);
  return (
    <PlcList className={rootClass} {...rest}>
      <CountryItemLoader />
      <CountryItemLoader />
      <CountryItemLoader />
      <CountryItemLoader />
      <CountryItemLoader />
      <CountryItemLoader />
      <CountryItemLoader />
      <CountryItemLoader />
      <CountryItemLoader />
      <CountryItemLoader />
    </PlcList>
  );
};

export { CountrySheetsList, CountrySheetsListLoader };
